import classNames from 'classnames';

import {
    GwgAccelerator,
    GwgBanner,
    GwgCarousel,
    useGetCurrentGwGAcceleratorEnrollmentsQuery,
} from '@udemy/career-academies';
import {useI18n} from '@udemy/i18n';
import {
    SubscriptionPlanProductType,
    SubscriptionPlansProvider,
    useConsumerSubscriptionGwGPlan,
    useConsumerSubscriptionGwGPlusPersonalPlan,
    useActiveSubscriptions,
} from '@udemy/subscription-graphql';

import {
    checkIsCombinedPPGwgSubscriber,
    checkIsGwgEnrolled,
    isSubscriptionLoading,
} from 'src/lib/gwg-helpers';

import styles from './gwg-section-pphp.module.less';

// ============================================================================
// GwG Banner Component
// ============================================================================

interface GwgBannerPPHPProps {
    className?: string;
}

/**
 * GwG Banner for Personal Plan Homepage.
 * Must be rendered inside GwgSubscriptionProvider.
 * Renders for PP+GwG subscribers only.
 */
export const GwgBannerPPHP = ({className}: GwgBannerPPHPProps) => {
    const {gettext} = useI18n();
    const gwgPlan = useConsumerSubscriptionGwGPlan();
    const combinedGwgPlan = useConsumerSubscriptionGwGPlusPersonalPlan();
    const isLoading = isSubscriptionLoading(gwgPlan, combinedGwgPlan);
    const isCombinedPPGwgSubscriber = checkIsCombinedPPGwgSubscriber(combinedGwgPlan);

    if (isLoading || !isCombinedPPGwgSubscriber) {
        return null;
    }

    return (
        <div className={className}>
            <GwgBanner
                mainTitle={gettext('Build job ready skills and Learn AI with Google')}
                subTitle={''}
                buttonText={gettext('All included content')}
                buttonHref={'/partner/google/learn-ai/'}
            />
        </div>
    );
};

// ============================================================================
// GwG Carousel Component
// ============================================================================

interface GwgCarouselPPHPProps {
    accelerator?: GwgAccelerator;
    className?: string;
}

/**
 * GwG Carousel for Personal Plan Homepage.
 * Must be rendered inside GwgSubscriptionProvider.
 * Renders for PP+GwG subscribers NOT enrolled in a GwG path.
 *
 * @param accelerator - GwG accelerator data (required for carousel to render)
 */
export const GwgCarouselPPHP = ({accelerator, className}: GwgCarouselPPHPProps) => {
    const {gettext} = useI18n();
    const combinedGwgPlan = useConsumerSubscriptionGwGPlusPersonalPlan();
    const {data: enrollmentData, isLoading: isEnrollmentLoading} =
        useGetCurrentGwGAcceleratorEnrollmentsQuery();
    const activeSubscriptions = useActiveSubscriptions();
    const currentlySubscribedPlan = activeSubscriptions?.[0];
    const isLoading =
        isSubscriptionLoading(currentlySubscribedPlan, combinedGwgPlan) || isEnrollmentLoading;
    const isCombinedPPGwgSubscriber = checkIsCombinedPPGwgSubscriber(combinedGwgPlan);
    const isGwgEnrolled = checkIsGwgEnrolled(enrollmentData);

    if (isLoading || !isCombinedPPGwgSubscriber || isGwgEnrolled || !accelerator) {
        return null;
    }

    return (
        <div className={classNames(className, styles['carousel-wrapper'])}>
            <h2 className="ud-heading-xxl">
                {gettext('Recommended to build job ready AI skills')}
            </h2>
            <GwgCarousel
                accelerator={accelerator}
                currentlySubscribedPlan={currentlySubscribedPlan}
            />
        </div>
    );
};

// ============================================================================
// Shared Provider
// ============================================================================

interface GwgSubscriptionProviderProps {
    children: React.ReactNode;
}

/**
 * Shared SubscriptionPlansProvider for GwG components on Personal Plan Homepage.
 * Wrap GwgBannerPPHP and GwgCarouselPPHP with this provider to share GQL calls.
 */
export const GwgSubscriptionProvider = ({children}: GwgSubscriptionProviderProps) => {
    return (
        <SubscriptionPlansProvider
            subscriptionPlanQueryVariables={[
                {filters: {productType: SubscriptionPlanProductType.GrowWithGoogle}},
                {
                    filters: {
                        productType:
                            SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
                    },
                },
            ]}
        >
            {children}
        </SubscriptionPlansProvider>
    );
};
